import { useNamespacedStore } from '~/compositions/store'
import {
  CLASSIFIED_FORM_NS,
  FormState
} from '~/store/modules/shared/classifieds/form/state'
import { computed, nextTick } from '@nuxtjs/composition-api'
import { useDeps } from '~/compositions/dependency-container'
import UserAgentService from '~/services/UserAgentService'
import {
  SET_INVALID_TRIGGERED,
  SET_IS_WARNING_CONFIRMATION_VISIBLE,
  SET_WARNING_CONFIRMATION_MESSAGE
} from '~/store/modules/shared/classifieds/form/mutation-types'
import { useSnackbar } from '~/compositions/snackbar'
import { useI18n } from '~/compositions/i18n'
import { useUserAgent } from '~/compositions/user-agent'
import { Field } from '~/models/classified/form'

export default function useClassifiedFormSubmit() {
  const {
    state: classifiedFormState,
    dispatch: classifiedFormDispatch,
    commit: classifiedFormCommit,
    getters: classifiedFormGetters
  } = useNamespacedStore<FormState>(CLASSIFIED_FORM_NS)
  const [userAgentService] = useDeps(UserAgentService)
  const snackbar = useSnackbar()
  const { t } = useI18n()
  const { isPc } = useUserAgent()

  const pageKey = computed(() => classifiedFormState.pageKey)

  const invalidTriggered = computed(() => classifiedFormState.invalidTriggered)

  const editMode = computed(() => classifiedFormState.editMode)

  const requiresTerms = computed(() => classifiedFormGetters('requiresTerms'))

  const termsAccepted = computed(() => classifiedFormState.termsAccepted)

  const requiresPayment = computed(() => classifiedFormState.requiresPayment)

  const noModelsForThisMake = computed(() =>
    classifiedFormGetters('noModelsForThisMake')
  )

  const hasPrice = computed(() => classifiedFormGetters('hasPrice'))

  const isRentalsForm = computed(() => classifiedFormGetters('isRentalsForm'))

  const isJobsForm = computed(() => classifiedFormGetters('isJobsForm'))

  const isWantedPartsForm = computed(() =>
    classifiedFormGetters('isWantedPartsForm')
  )

  const isCarsForm = computed(() => classifiedFormGetters('isCarsForm'))

  const hasLeasingOfferType = computed(() =>
    classifiedFormGetters('hasLeasingOfferType')
  )

  const selectedModel = computed(
    () => classifiedFormState.fieldValues?.makemodel?.model
  )

  const noModelSelectedForCar = computed(() => {
    if (!isCarsForm.value) {
      return false
    }

    const makemodelField = classifiedFormGetters('getAllFields')?.find(
      (f: Field) => f.name === 'makemodel'
    )

    if (!makemodelField) {
      return false
    }

    return (
      !makemodelField.disabled &&
      !noModelsForThisMake.value &&
      !selectedModel.value
    )
  })

  async function submitData(options?: { scroll: boolean }) {
    if (editMode.value) {
      await classifiedFormDispatch('submitEditClassified', options)
    } else {
      const tab = requiresPayment.value ? 'charges' : null
      await classifiedFormDispatch('submitNewClassified', tab)
    }
  }

  function onWarningConfirmationModalConfirm() {
    classifiedFormCommit(SET_IS_WARNING_CONFIRMATION_VISIBLE, false)
    submitData()
  }

  function onWarningConfirmationModalCancel() {
    const name = noModelSelectedForCar.value ? 'vehicle_details' : 'offer_info'
    const offerInfo = document.getElementsByName(name)[0]

    if (offerInfo) {
      offerInfo.scrollIntoView({ behavior: 'smooth' })
    }

    classifiedFormCommit(SET_IS_WARNING_CONFIRMATION_VISIBLE, false)
  }

  async function onInvalid(e: any) {
    if (!invalidTriggered.value) {
      classifiedFormCommit(SET_INVALID_TRIGGERED, true)

      await nextTick()

      const yOffset = -80 // 52 for header and 28 to show label
      const y =
        e.target.getBoundingClientRect().top + window.pageYOffset + yOffset

      window.scrollTo({ top: y, behavior: 'smooth' })
    }
  }

  async function submitForm(options: { scroll: boolean }) {
    const formElement = document.getElementById(
      `form-${pageKey.value}`
    ) as HTMLFormElement

    // checking for firefox here because it already has a buggy behavior for error scrolling https://bugzilla.mozilla.org/show_bug.cgi?id=643758
    if (formElement && !userAgentService.isFirefox()) {
      formElement.removeAttribute('novalidate')
      formElement.addEventListener('invalid', onInvalid, true)
    }

    classifiedFormCommit(SET_INVALID_TRIGGERED, false)
    const clientValidationStatus = await classifiedFormDispatch(
      'validateClient'
    )

    if (
      formElement &&
      !(userAgentService.isFirefox() && !isPc.value) &&
      (!clientValidationStatus || !formElement.reportValidity())
    ) {
      // mobile version of firefox not showing validation errors (it doesn't even scroll) https://bugzilla.mozilla.org/show_bug.cgi?id=1510450
      // client side navigation return an error
      formElement.removeEventListener('invalid', onInvalid)
      formElement.setAttribute('novalidate', '')
      return false
    }

    if (requiresTerms.value && !termsAccepted.value) {
      snackbar.error(
        t('you must accept our terms and conditions to submit a classified')
      )

      return false
    }

    if (
      !hasPrice.value &&
      !isRentalsForm.value &&
      !isJobsForm.value &&
      !isWantedPartsForm.value &&
      !hasLeasingOfferType.value
    ) {
      classifiedFormCommit(SET_WARNING_CONFIRMATION_MESSAGE, {
        title: t('missing price data'),
        text: t(
          editMode.value
            ? 'fill in the price in the offer details to make the classified available to users'
            : 'you have not set a price and your classified appears as unavailable'
        )
      })
      classifiedFormCommit(SET_IS_WARNING_CONFIRMATION_VISIBLE, true)
    } else if (noModelSelectedForCar.value) {
      classifiedFormCommit(SET_WARNING_CONFIRMATION_MESSAGE, {
        title: t('no_model_title'),
        text: t('no_model_warning')
      })
      classifiedFormCommit(SET_IS_WARNING_CONFIRMATION_VISIBLE, true)
    } else {
      await submitData(options)
    }
  }

  return {
    onWarningConfirmationModalConfirm,
    onWarningConfirmationModalCancel,
    submitForm
  }
}
